import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import useReengagement from '@/src/Share/components/hooks/useReengagement';

/*
 * Reengage dialog (v2 skin) — send every unique share destination in the
 * event a fresh link to their own gallery, with a custom message. Same
 * useReengagement state machine as the classic modal.
 */

interface ReengageDialogProps {
    activationId: number;
    onClose: () => void;
}

export default function ReengageDialog({
    activationId,
    onClose,
}: ReengageDialogProps) {
    const {
        preview,
        isLoadingPreview,
        loadPreview,
        activeSend,
        isStarting,
        start,
        error,
    } = useReengagement(activationId);

    const [channels, setChannels] = useState<'both' | 'text' | 'email'>(
        'both',
    );
    const [textBody, setTextBody] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        loadPreview();
    }, [loadPreview]);

    const wantsText = channels !== 'email';
    const wantsEmail = channels !== 'text';

    const recipientCount = useMemo(() => {
        if (!preview) return 0;
        if (channels === 'text') return preview.textCount;
        if (channels === 'email') return preview.emailCount;
        return preview.total;
    }, [preview, channels]);

    const canSend =
        recipientCount > 0 &&
        (!wantsText || textBody.trim().length > 0) &&
        (!wantsEmail || emailBody.trim().length > 0) &&
        !isStarting;

    const onSend = async () => {
        if (!canSend) return;
        if (!confirming) {
            setConfirming(true);
            return;
        }
        setConfirming(false);
        await start({
            channels,
            textBody: wantsText ? textBody : undefined,
            emailSubject: wantsEmail ? emailSubject : undefined,
            emailBody: wantsEmail ? emailBody : undefined,
        });
    };

    const progress = activeSend
        ? activeSend.sentCount +
          activeSend.failedCount +
          activeSend.skippedCount
        : 0;
    const progressPercent = activeSend?.totalCount
        ? Math.round((progress / activeSend.totalCount) * 100)
        : 0;
    const running =
        activeSend?.status === 'pending' || activeSend?.status === 'running';

    const label =
        'mb-1.5 block text-[12px] font-semibold text-muted-foreground';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/45"
                onClick={running ? undefined : onClose}
            />
            <div className="relative z-10 max-h-[90vh] w-full max-w-[480px] overflow-y-auto rounded-[14px] border border-border bg-background p-[22px] shadow-[0_18px_50px_-12px_rgba(20,23,29,0.45)]">
                <div className="mb-1 flex items-center justify-between">
                    <span className="ps-label">Reengage guests</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] text-faint hover:text-foreground"
                    >
                        ✕
                    </button>
                </div>

                {!activeSend && (
                    <>
                        <p className="mb-4 mt-1 text-[12.5px] text-muted-foreground">
                            Everyone this event shared to gets a fresh link to
                            their own gallery, with your message.
                        </p>

                        <div className="mb-4 rounded-[9px] bg-sunk px-3 py-2 text-[12.5px] text-muted-foreground">
                            {isLoadingPreview && 'Loading recipients…'}
                            {preview && (
                                <>
                                    <b className="font-semibold text-foreground">
                                        {preview.total.toLocaleString()} unique
                                        recipient
                                        {preview.total === 1 ? '' : 's'}
                                    </b>{' '}
                                    — {preview.textCount} SMS ·{' '}
                                    {preview.emailCount} email
                                    {preview.optedOutCount > 0 &&
                                        ` · ${preview.optedOutCount} opted out (skipped)`}
                                </>
                            )}
                        </div>

                        <div className="mb-4">
                            <label className={label}>Send via</label>
                            <div className="flex gap-1.5">
                                {(
                                    [
                                        ['both', 'Text + Email'],
                                        ['text', 'Text only'],
                                        ['email', 'Email only'],
                                    ] as const
                                ).map(([value, text]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => {
                                            setChannels(value);
                                            setConfirming(false);
                                        }}
                                        className={
                                            'cursor-pointer rounded-full px-3 py-[5px] font-[inherit] text-[12px] font-semibold ' +
                                            (channels === value
                                                ? 'border border-transparent bg-brand-soft text-brand-ink'
                                                : 'border border-border bg-transparent text-muted-foreground')
                                        }
                                    >
                                        {text}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {wantsText && (
                            <div className="mb-4">
                                <label className={label}>Text message</label>
                                <textarea
                                    className="ps-input w-full"
                                    rows={3}
                                    value={textBody}
                                    placeholder="Relive the party! Your photos are waiting:"
                                    onChange={(e) => {
                                        setTextBody(e.target.value);
                                        setConfirming(false);
                                    }}
                                />
                                <p className="mt-1 text-[11.5px] text-faint">
                                    Each recipient's gallery link is appended
                                    automatically.
                                </p>
                            </div>
                        )}

                        {wantsEmail && (
                            <>
                                <div className="mb-4">
                                    <label className={label}>
                                        Email subject{' '}
                                        <span className="font-normal text-faint">
                                            (optional — falls back to the
                                            gallery's)
                                        </span>
                                    </label>
                                    <input
                                        className="ps-input w-full"
                                        value={emailSubject}
                                        placeholder="Your photos are waiting!"
                                        onChange={(e) => {
                                            setEmailSubject(e.target.value);
                                            setConfirming(false);
                                        }}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className={label}>Email body</label>
                                    <textarea
                                        className="ps-input w-full font-mono text-[12px]"
                                        rows={6}
                                        value={emailBody}
                                        placeholder="Plain text or HTML. Use *|SHAREURL|* to place the gallery link."
                                        onChange={(e) => {
                                            setEmailBody(e.target.value);
                                            setConfirming(false);
                                        }}
                                    />
                                    <p className="mt-1 text-[11.5px] text-faint">
                                        Plain text or an HTML template.{' '}
                                        <code>*|SHAREURL|*</code> places the
                                        gallery link; without it the link is
                                        appended at the end.
                                    </p>
                                </div>
                            </>
                        )}

                        {error && (
                            <p className="mb-3 text-[12px] font-semibold text-attention">
                                {error}
                            </p>
                        )}

                        <div className="flex items-center justify-end gap-2.5">
                            {confirming && (
                                <span className="text-[11.5px] text-faint">
                                    This cannot be undone.
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={onSend}
                                disabled={!canSend}
                                className={
                                    'cursor-pointer rounded-full border-0 px-4 py-1.5 font-[inherit] text-[12px] font-semibold ' +
                                    (canSend
                                        ? confirming
                                            ? 'bg-attention text-white'
                                            : 'bg-brand text-brand-foreground'
                                        : 'cursor-not-allowed bg-secondary text-faint')
                                }
                            >
                                {isStarting
                                    ? 'Starting…'
                                    : confirming
                                    ? `Confirm — send to ${recipientCount}`
                                    : `Send to ${recipientCount} recipient${
                                          recipientCount === 1 ? '' : 's'
                                      }`}
                            </button>
                        </div>
                    </>
                )}

                {activeSend && (
                    <div className="mt-2">
                        <p className="mb-2.5 text-[12.5px] text-muted-foreground">
                            {activeSend.status === 'complete' &&
                                'Done — all messages processed.'}
                            {activeSend.status === 'failed' &&
                                'Finished with some failures.'}
                            {running &&
                                'Sending… you can close this window, the send continues on the server.'}
                        </p>
                        <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                            <div
                                className="h-full rounded-full bg-brand transition-all"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <p className="text-[12.5px] tabular-nums text-muted-foreground">
                            {activeSend.sentCount} sent
                            {activeSend.skippedCount > 0 &&
                                ` · ${activeSend.skippedCount} skipped (opted out)`}
                            {activeSend.failedCount > 0 && (
                                <span className="font-semibold text-attention">
                                    {' '}
                                    · {activeSend.failedCount} failed
                                </span>
                            )}{' '}
                            · {activeSend.totalCount} total
                        </p>
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={onClose}
                                className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                            >
                                {running ? 'Close (keeps sending)' : 'Close'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
